import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getAllFieldsTouched } from '../../../../../../common';
import useAreScreeningDatesRequired from './useAreScreeningDatesRequired';
import {
  getChangedFieldNames,
  getChangedSavedFieldValues,
  getMergedPatchData,
  getSavedFieldValues,
  getValuesWithFieldOverride,
  isDateFieldValid,
  isFieldSchemaValid,
} from './utils';
import { intakeQuestionnaireActions } from '../../../../../../../store';

import type { AppDispatch } from '../../../../../../../store';
import type { FormikProps } from 'formik';
import type { MedicalFormValues } from '../types';
import type {
  MedicalInfoFieldName,
  MedicalInfoFieldValue,
  MedicalInfoSavedFieldValues,
} from './types';

export default function useSaveMedicalInfoFieldOnBlur(
  formik: FormikProps<MedicalFormValues>,
  initialValues: MedicalFormValues,
) {
  const dispatch = useDispatch<AppDispatch>();
  const areScreeningDatesRequired = useAreScreeningDatesRequired();
  const { getAccessTokenSilently } = useAuth0();
  const { intakeQuestionnaireId } = useParams<'intakeQuestionnaireId'>();
  const savedFieldValuesRef = useRef<MedicalInfoSavedFieldValues>(
    getSavedFieldValues(initialValues),
  );
  const savingPromiseRef = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    savedFieldValuesRef.current = getSavedFieldValues(initialValues);
  }, [initialValues]);

  const performMedicalInfoSave = useCallback(
    async (values: MedicalFormValues) => {
      if (!intakeQuestionnaireId) {
        return;
      }

      const previousSave = savingPromiseRef.current;
      const currentSave = (async () => {
        try {
          await previousSave;
        } catch {
          // Swallow prior save errors so this save still proceeds.
        }

        const changedSavedValues = getChangedSavedFieldValues(values, savedFieldValuesRef.current);
        if (Object.keys(changedSavedValues).length === 0) {
          return;
        }

        const fieldNames = getChangedFieldNames(changedSavedValues);
        const resultAction = await dispatch(
          intakeQuestionnaireActions.updateIntakeMedicalInfo({
            intakeQuestionnaireId,
            data: getMergedPatchData(values, fieldNames),
            getAccessTokenSilently,
          }),
        );

        if (!intakeQuestionnaireActions.updateIntakeMedicalInfo.fulfilled.match(resultAction)) {
          return;
        }

        const errors = await formik.validateForm(values);
        formik.setErrors(errors);
        formik.setTouched(getAllFieldsTouched(values), false);
        savedFieldValuesRef.current = {
          ...savedFieldValuesRef.current,
          ...changedSavedValues,
        };
      })();

      savingPromiseRef.current = currentSave;
      await currentSave;
    },
    [dispatch, formik, getAccessTokenSilently, intakeQuestionnaireId],
  );

  const saveFieldOnBlur = useCallback(
    async (name: MedicalInfoFieldName, value?: MedicalInfoFieldValue) => {
      const values = getValuesWithFieldOverride(formik.values, name, value);

      if (!isDateFieldValid(name, values)) {
        return;
      }
      if (!(await isFieldSchemaValid(name, values, areScreeningDatesRequired))) {
        return;
      }

      await performMedicalInfoSave(values);
    },
    [areScreeningDatesRequired, formik, performMedicalInfoSave],
  );

  return useMemo(() => ({ saveFieldOnBlur }), [saveFieldOnBlur]);
}
